import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useQuery } from "@tanstack/react-query";
import { toast } from "sonner";
import { Loader2, Sparkles, Wrench } from "lucide-react";

import { cn } from "../../../lib/utils";
import { queryClient } from "../../../lib/queryClient";
import useRetryFetch from "../../../hooks/useRetryFetch";
import { autoenginuityGaps, type AutoenginuityGap } from "../../../api/mentalModels";
import { createTrait, listTraits, type UserTrait } from "../../../api/userTraits";
import { Button } from "../../ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../ui/dialog";
import { Textarea } from "../../ui/textarea";

/**
 * ICDF §5.1 Autoenginuity — formative assessment of how I engineer myself.
 * The check-in ritual (worked / failed / missing) AI-extracts candidate
 * capability gaps; the ones the user adopts become aspiration UserTraits
 * tagged source:"autoenginuity". The strip shows the gaps being closed —
 * the closing plan is the Cockpit path (gap → life project → journey).
 */

const TRAIT_LEVELS = ["Aspiration", "Emerging", "Developing", "Embodied"];

export const isGapTrait = (t: UserTrait) =>
  t.category === "aspiration" &&
  (t.source === "autoenginuity" || (t.tags || []).includes("autoenginuity") || (t.tags || []).includes("reality-shaping"));

const AutoenginuityGaps = () => {
  const { t, i18n } = useTranslation();
  const retryFetch = useRetryFetch();

  const { data: traits } = useQuery({
    queryKey: ["cockpit-gap-traits"],
    queryFn: () => listTraits(retryFetch) as Promise<UserTrait[]>,
    staleTime: 30_000,
  });
  const gaps = (traits ?? []).filter(isGapTrait);

  const [ritualOpen, setRitualOpen] = useState(false);
  const [worked, setWorked] = useState("");
  const [failed, setFailed] = useState("");
  const [missing, setMissing] = useState("");
  const [extracting, setExtracting] = useState(false);
  const [candidates, setCandidates] = useState<AutoenginuityGap[] | null>(null);
  const [picked, setPicked] = useState<Record<number, boolean>>({});
  const [adopting, setAdopting] = useState(false);

  const resetRitual = () => {
    setWorked("");
    setFailed("");
    setMissing("");
    setCandidates(null);
    setPicked({});
  };

  const extract = async () => {
    setExtracting(true);
    try {
      const res = await autoenginuityGaps(retryFetch, {
        worked,
        failed,
        missing,
        language: i18n.language?.split("-")[0] || "en",
      });
      setCandidates(res.gaps);
      const all: Record<number, boolean> = {};
      res.gaps.forEach((_, i) => (all[i] = true));
      setPicked(all);
    } catch (e: any) {
      toast.error(e.message || t("cockpit_gaps_extract_failed", "Could not read your answers — try again."));
    } finally {
      setExtracting(false);
    }
  };

  const adopt = async () => {
    if (!candidates) return;
    const chosen = candidates.filter((_, i) => picked[i]);
    if (!chosen.length) return;
    setAdopting(true);
    try {
      for (const gap of chosen) {
        await createTrait(retryFetch, {
          name: gap.name,
          emoji: gap.emoji,
          description: gap.description,
          category: "aspiration",
          source: "autoenginuity",
          tags: ["autoenginuity"],
        });
      }
      queryClient.invalidateQueries({ queryKey: ["cockpit-gap-traits"] });
      toast.success(t("cockpit_gaps_adopted", "Added to the gaps you're closing."));
      setRitualOpen(false);
      resetRitual();
    } catch (e: any) {
      toast.error(e.message || "Could not save");
    } finally {
      setAdopting(false);
    }
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <p className="text-[11px] font-semibold uppercase tracking-widest text-muted-foreground/70 flex items-center gap-1.5">
          <Wrench className="w-3 h-3 text-amber-600" />
          {t("cockpit_gaps_title", "Gaps I'm closing")}
        </p>
        <button
          onClick={() => setRitualOpen(true)}
          className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-primary"
        >
          <Sparkles className="w-3 h-3" />
          {t("cockpit_gaps_checkin", "Autoenginuity check-in")}
        </button>
      </div>

      {gaps.length > 0 ? (
        <div className="flex flex-col gap-2">
          {/* Each gap as a quiet progress row — the bar is the trait level
              (Aspiration → Embodied), not a score */}
          {gaps.map((g) => (
            <div
              key={g._id}
              title={`${g.description ?? ""} · ${TRAIT_LEVELS[g.level]}`.replace(/^ · /, "")}
              className="flex items-center gap-3 rounded-[13px] border border-border/70 bg-card px-[15px] py-[11px]"
            >
              <span className="flex-1 min-w-0 truncate text-[12.5px] text-foreground/80">
                {g.emoji && <span className="me-1.5">{g.emoji}</span>}
                {g.name}
                {(g.pwfIds?.length ?? 0) > 0 && (
                  <span className="ms-2 text-[9px] uppercase tracking-wide rounded bg-amber-100 text-amber-800 px-1 py-0.5">
                    {t("cockpit_gaps_in_plan", "in a plan")}
                  </span>
                )}
              </span>
              <div className="w-[110px] flex-none h-1 rounded-full bg-gray-100 overflow-hidden">
                <div
                  className={cn("h-full rounded-full", g.level > 0 ? "bg-primary" : "bg-primary/40")}
                  style={{ width: `${Math.max(8, (g.level / 3) * 100)}%` }}
                />
              </div>
            </div>
          ))}
        </div>
      ) : (
        <p className="text-xs text-muted-foreground/80">
          {t(
            "cockpit_gaps_empty",
            "No gaps on the list. Run an Autoenginuity check-in to look at how your process served you lately."
          )}
        </p>
      )}

      <Dialog open={ritualOpen} onOpenChange={(o) => { setRitualOpen(o); if (!o) resetRitual(); }}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{t("cockpit_gaps_ritual_title", "Autoenginuity check-in")}</DialogTitle>
          </DialogHeader>

          {!candidates ? (
            <div className="space-y-3">
              <div>
                <label className="text-xs font-medium text-muted-foreground">
                  {t("cockpit_gaps_q_worked", "What worked this period?")}
                </label>
                <Textarea value={worked} onChange={(e) => setWorked(e.target.value)} rows={2} className="mt-1" />
              </div>
              <div>
                <label className="text-xs font-medium text-muted-foreground">
                  {t("cockpit_gaps_q_failed", "Where did my process fail me?")}
                </label>
                <Textarea value={failed} onChange={(e) => setFailed(e.target.value)} rows={2} className="mt-1" />
              </div>
              <div>
                <label className="text-xs font-medium text-muted-foreground">
                  {t("cockpit_gaps_q_missing", "Which capability was missing?")}
                </label>
                <Textarea value={missing} onChange={(e) => setMissing(e.target.value)} rows={2} className="mt-1" />
              </div>
              <Button
                onClick={extract}
                disabled={extracting || (!worked.trim() && !failed.trim() && !missing.trim())}
              >
                {extracting && <Loader2 className="w-3.5 h-3.5 animate-spin me-1.5" />}
                {t("cockpit_gaps_extract", "Find the gaps")}
              </Button>
            </div>
          ) : (
            <div className="space-y-2">
              <p className="text-xs text-muted-foreground">
                {t("cockpit_gaps_pick", "Keep the gaps that ring true — they join your Compass as aspirations.")}
              </p>
              {candidates.map((gap, i) => (
                <label
                  key={i}
                  className={cn(
                    "flex items-start gap-2 rounded-lg border p-3 text-sm cursor-pointer",
                    picked[i] ? "border-primary bg-primary/5" : "border-border"
                  )}
                >
                  <input
                    type="checkbox"
                    checked={Boolean(picked[i])}
                    onChange={(e) => setPicked({ ...picked, [i]: e.target.checked })}
                    className="mt-0.5"
                  />
                  <span>
                    <span className="font-medium">
                      {gap.emoji && <span className="me-1">{gap.emoji}</span>}
                      {gap.name}
                    </span>
                    {gap.description && (
                      <p className="text-xs text-muted-foreground mt-0.5">{gap.description}</p>
                    )}
                    {gap.why && <p className="text-xs text-amber-700 mt-0.5">{gap.why}</p>}
                  </span>
                </label>
              ))}
              <div className="flex items-center gap-2 pt-1">
                <Button onClick={adopt} disabled={adopting || !Object.values(picked).some(Boolean)}>
                  {adopting && <Loader2 className="w-3.5 h-3.5 animate-spin me-1.5" />}
                  {t("cockpit_gaps_adopt", "Adopt selected")}
                </Button>
                <button
                  onClick={() => setCandidates(null)}
                  className="text-xs text-muted-foreground hover:text-foreground"
                >
                  {t("back", "Back")}
                </button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AutoenginuityGaps;
